import config from '../config'
import { RouterRegistry } from './routerRegistry'
import { useStore } from '../store'

// Only log config and methods in staging and execute pendo actions only in prod

// Check if we should log (all environments except production)
const shouldLog = config.mode !== 'production'

const isPendoDisabled = localStorage?.getItem('pendoDisabled') === 'true'

// Check if we should execute pendo methods (only prod environments)
const shouldExecute = config.mode === 'production' && !isPendoDisabled

// Simple logging helper
const log = (message: string, data?: any) => {
  if (shouldLog) console.log(`PENDO: ${message}`, data || '')
}

// Extend the Window interface to include Pendo
declare global {
  interface Window {
    pendo?: {
      initialize: (config: PendoConfig) => void
      identify: (config: PendoConfig) => void
      isReady: () => boolean
      clearSession: () => void
      teardown: () => void
      track: (eventName: string, properties?: Record<string, any>) => void
      accountId: string
    }
  }
}

export interface PendoEntity {
  id: string | null
  [key: string]: any
}

export type PendoVisitor = PendoEntity

export type PendoAccount = PendoEntity

export type PendoParentAccount = PendoEntity

export interface PendoConfig {
  visitor: PendoVisitor
  account?: PendoAccount
  parentAccount: PendoParentAccount
  disableCookies?: boolean
  disablePersistence?: boolean
}

/**
 * Validate if Pendo action can proceed (not support user and method available)
 */
function canProceedWithPendoAction(method: Exclude<keyof NonNullable<Window['pendo']>, 'accountId'>): boolean {
  const store = useStore()
  const isSupportAgent = store.getters['auth/isSupportAgent']

  if (isSupportAgent) {
    log(`Skip ${method} - support user`)
    return false
  }

  if (!window.pendo?.[method]) {
    console.warn(`PENDO: ${method} not available`)
    return false
  }

  return true
}

/**
 * Validate and execute a Pendo action with logging
 */
function executePendoAction<T extends Exclude<keyof NonNullable<Window['pendo']>, 'accountId'>>(
  method: T,
  config?: PendoConfig
): ReturnType<NonNullable<Window['pendo']>[T]> | undefined {
  try {
    log(`${method}`, config)
    if (!canProceedWithPendoAction(method)) return

    if (shouldExecute) {
      return config ? window.pendo![`${method}`](config) : window.pendo![`${method}`]()
    }
  } catch (error) {
    console.error(`PENDO: ${method} failed`, error)
  }
}

/**
 * Build Pendo configuration from store data
 */
function buildPendoConfig(): PendoConfig | null {
  try {
    const store = useStore()

    const auth = store.getters['auth/getAuth']
    const companyId = auth.companyId
    const userId = auth.userId

    if (!userId || !companyId) {
      console.warn('PENDO: Missing user/company data')
      return null
    }

    const config: PendoConfig = {
      visitor: { id: userId },
      parentAccount: { id: companyId },
      account: { id: null },
    }
    const router = RouterRegistry.getRouter()
    const locationId = router?.currentRoute?.value?.params?.location_id
    if (locationId) {
      config.account = { id: `${companyId}::${locationId}` }
    }

    return config
  } catch (error) {
    console.error('PENDO: Config build failed', error)
    return null
  }
}

/**
 * Initialize Pendo with visitor and account data from store
 */
export function initializePendo(): void {
  const config = buildPendoConfig()
  if (!config) return

  if (window.pendo?.isReady?.()) {
    log('Already initialized')
    return
  }

  executePendoAction('initialize', { ...config, disableCookies: true, disablePersistence: true })
}

/**
 * Identify the user and account in Pendo using store data
 * Uses companyId as parent account id, locationId as account id, and userId as visitor id
 */
export function identifyPendo(): void {
  const config = buildPendoConfig()
  if (!config) return

  if (config.account?.id && config.account?.id === window.pendo?.accountId) {
    log('Account already identified')
    return
  }

  executePendoAction('identify', config)
}

/**
 * Clear the current Pendo session
 */
export function clearPendoSession(): void {
  executePendoAction('clearSession')
}

/**
 * Teardown Pendo agent and clean up all timers and listeners
 */
export function teardownPendo(): void {
  executePendoAction('teardown')
}
